import { Label, tokens } from '@fluentui/react-components';
import {
  PanelBottomExpandRegular,
  PanelTopExpandRegular,
} from '@fluentui/react-icons';
import { parseOtherInfo, type IField } from '@moko/utils';
import { useOtherInfoContext } from '../OtherInfoContext';

import { RepeatingPanelToolbar } from '../RepeatingPanelToolbar';
import { EntityRepeatingPanelToolbar } from '../EntityRepeatingPanelToolbar';
import { MobileEntityRepeatingPanel } from '../MobileEntityRepeatingPanel';
import { MobileRepeatingPanel } from '../MobileRepeatingPanel';
import { FieldList } from '../FieldList';
import { usePanelFieldsStyles } from '../panelFieldsStyles';
import {
  getPanelColumns,
  getPanelMaxWidth,
  panelChromeEnabled,
  panelHeaderTitleEnabled,
  panelSectionTitleEnabled,
} from '../panelLayout';

interface PanelFieldProps {
  field: IField;
  fields: IField[];
}

export const PanelField = ({ field, fields }: PanelFieldProps) => {
  const styles = usePanelFieldsStyles();
  const {
    params,
    otherInfo,
    getRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel,
    handleRepeatingPanelAction,
    ensureEntityRepeatingEntry,
    rerenderOtherInfo,
  } = useOtherInfoContext();
  const columns = getPanelColumns(field.options);

  const { relatedEntities } = params;
  const isEntityRepeating = field.repeatingMode
    ?.toString()
    ?.trim()
    .startsWith('E');
  const isManualRepeating = field.repeatingMode
    ?.toString()
    ?.trim()
    .startsWith('M');

  const useNewRepeatingPanels =
    params.mode === 'mobile' || params.mode === 'desktop-split';

  if (useNewRepeatingPanels && isEntityRepeating) {
    return <MobileEntityRepeatingPanel field={field} fields={fields} />;
  }

  if (useNewRepeatingPanels && isManualRepeating) {
    return <MobileRepeatingPanel field={field} fields={fields} />;
  }

  const panelMaxWidth = field.fullWidth ? '100%' : getPanelMaxWidth(columns);

  if (!panelChromeEnabled(field.options)) {
    const showSectionTitle =
      panelSectionTitleEnabled(field.options) && Boolean(field.label);
    const showHeaderTitle =
      !showSectionTitle &&
      panelHeaderTitleEnabled(field.options) &&
      Boolean(field.label);
    return (
      <div
        id={'field_' + field.name}
        className={showSectionTitle ? styles.section : styles.container}
        style={{ maxWidth: panelMaxWidth }}
      >
        {showSectionTitle ? (
          <Label
            className={
              field.options &&
              typeof field.options === 'object' &&
              (field.options as { strongTitle?: boolean }).strongTitle
                ? styles.sectionTitleStrong
                : styles.sectionTitle
            }
          >
            {field.label}
          </Label>
        ) : showHeaderTitle ? (
          <Label className={styles.headerTitle}>{field.label}</Label>
        ) : null}
        <FieldList fields={fields} parentName={field.name} />
      </div>
    );
  }

  const handlePanelToggle = (panelName: string) => {
    params.metadata.fields = params.metadata.fields.map((f) => {
      if (f.name === panelName) {
        return { ...f, isCollapsed: !f.isCollapsed };
      }
      return f;
    });
    rerenderOtherInfo();
  };

  return (
    <div
      id={'field_' + field.name}
      style={{
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '5px',
        width: '100%',
        maxWidth: panelMaxWidth,
        minWidth: 0,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        rowGap: '14px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '5px 10px',
          backgroundColor: tokens.colorBrandBackgroundInvertedSelected,
        }}
      >
        <Label>{field.label}</Label>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: 'auto',
          }}
        >
          {field.repeatingMode?.toString()?.trim().startsWith('M') && (
            <RepeatingPanelToolbar
              currentIndex={getRepeatingPanelIndex(field.name)}
              total={parseOtherInfo(otherInfo)[field.name]?.length ?? 0}
              readOnly={params.readOnly}
              onAction={(action) =>
                handleRepeatingPanelAction(field.name, action)
              }
            />
          )}
          {field.repeatingMode?.toString()?.trim().startsWith('E') && (
            <EntityRepeatingPanelToolbar
              entities={
                relatedEntities?.map((entity) => ({
                  id: entity.id,
                  entityInfo: entity.entityInfo,
                })) ?? []
              }
              selectedEntityId={currentElementInEntityRepeatingPanel}
              onEntitySelection={(entityId) => {
                ensureEntityRepeatingEntry(field.name, entityId);
              }}
            />
          )}
          {!field.isCollapsed ? (
            <PanelBottomExpandRegular
              onClick={() => handlePanelToggle(field.name)}
            />
          ) : (
            <PanelTopExpandRegular
              onClick={() => handlePanelToggle(field.name)}
            />
          )}
        </div>
      </div>
      <div
        className={styles.container}
        style={{
          padding: '0 10px 10px',
          display: 'flex',
          flexDirection: 'column',
          rowGap: '14px',
          boxSizing: 'border-box',
        }}
      >
        {!field.isCollapsed && (
          <FieldList fields={fields} parentName={field.name} />
        )}
      </div>
    </div>
  );
};
